import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowLeft, MapPin, Clock, ShieldCheck, CheckCircle2 } from "lucide-react";
import PublicLayout from "@/layouts/PublicLayout";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useDocumentTitle } from "@/lib/useDocumentTitle";

/**
 * Unified Phase-3 checkout. Reads ?service=<slug or id> and optional
 * package + addons from query/state, then routes into the right transaction
 * type:
 *  • local → POST /bookings (needs slot + address)
 *  • digital → POST /digital-orders (no slot)
 *  • consultation → POST /bookings (kind=consultation, slot required)
 */
export default function CheckoutPage() {
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = location;
  const { user } = useAuth();
  useDocumentTitle("Checkout");

  const [service, setService] = useState(state?.service || null);
  const [pkgId, setPkgId] = useState(state?.pkgId || sp.get("package") || null);
  const [addonIds, setAddonIds] = useState(state?.addonIds || (sp.get("addons") ? sp.get("addons").split(",") : []));
  const [pkg, setPkg] = useState(null);
  const [addonDocs, setAddonDocs] = useState([]);

  // Local booking state
  const [addrLine1, setAddrLine1] = useState("");
  const [addrLine2, setAddrLine2] = useState("");
  const [addrCity, setAddrCity] = useState("");
  const [addrState, setAddrState] = useState("FL");
  const [addrZip, setAddrZip] = useState("");
  const [accessNotes, setAccessNotes] = useState("");
  const [instructions, setInstructions] = useState("");
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [dayIdx, setDayIdx] = useState(0);

  const [submitting, setSubmitting] = useState(false);

  const serviceKey = sp.get("service") || state?.service?.id;
  const dealId = sp.get("deal");
  const created = useRef(null);
  const [deal, setDeal] = useState(null);
  useEffect(() => {
    if (dealId && serviceKey) api.get(`/deals/for-service/${serviceKey}`).then((r) => setDeal(r.data.deal?.id === dealId ? r.data.deal : null)).catch(() => setDeal(null));
  }, [dealId, serviceKey]);

  useEffect(() => {
    if (!user) {
      navigate(`/login?next=${encodeURIComponent(location.pathname + window.location.search)}`);
      return;
    }
    if (!serviceKey) {
      navigate("/services");
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/services/${serviceKey}`);
        setService(data.service);
        if (pkgId) setPkg((data.packages || []).find((p) => p.id === pkgId) || null);
        setAddonDocs((data.addons || []).filter((a) => addonIds.includes(a.id)));
      } catch (err) {
        toast.error("Could not load service");
        navigate("/services");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceKey]);

  const isLocalKind = service?.kind === "local" || service?.service_type === "consultation" || service?.service_type === "recurring_service";
  const needsSlot = service?.kind === "local" || service?.service_type === "consultation";

  useEffect(() => {
    if (!service || !needsSlot) return;
    (async () => {
      setSlotsLoading(true);
      try {
        const today = new Date();
        const start = today.toISOString().slice(0, 10);
        const end = new Date(today.getTime() + 6 * 86400000).toISOString().slice(0, 10);
        const { data } = await api.get(`/bookings/services/${service.id}/slots`, { params: { start_date: start, end_date: end } });
        setSlots(data.days || []);
      } catch (err) {
        setSlots([]);
      } finally {
        setSlotsLoading(false);
      }
    })();
  }, [service, needsSlot]);

  const pricing = useMemo(() => {
    if (!service) return { subtotal: 0, addons: 0, total: 0 };
    let subtotal = pkg ? Number(pkg.price || 0) : Number(service.base_price || 0);
    let ad = 0;
    addonDocs.forEach((a) => { ad += Number(a.price || 0); });
    const off = deal ? Math.round(Math.min(deal.discount_type === "percent" ? (subtotal * deal.discount_value) / 100 : deal.discount_value, subtotal) * 100) / 100 : 0;
    return {
      subtotal,
      addons: ad,
      dealOff: off,
      total: Math.round((subtotal + ad - off) * 100) / 100,
      currency: service.currency || "USD",
    };
  }, [service, pkg, addonDocs, deal]);

  const submitBooking = async () => {
    if (!selectedSlot) return toast.error("Pick a time slot");
    if (service.kind === "local" && (!addrLine1 || !addrCity || !addrZip)) return toast.error("Address is required");
    setSubmitting(true);
    try {
      const payload = {
        service_id: service.id,
        package_id: pkgId,
        addon_ids: addonIds,
        quantity_map: Object.fromEntries(addonIds.map((id) => [id, 1])),
        scheduled_start: selectedSlot.start,
        instructions,
        deal_id: deal?.id || null,
      };
      if (service.kind === "local") {
        payload.address = {
          line1: addrLine1, line2: addrLine2, city: addrCity, state: addrState, zip_code: addrZip, access_instructions: accessNotes,
        };
      }
      const { data } = created.current?.type === "booking" ? { data: { id: created.current.id } } : await api.post("/bookings", payload);
      created.current = { type: "booking", id: data.id };
      const { data: session } = await api.post("/payments/checkout", {
        purpose: service.service_type === "consultation" ? "consultation" : "booking",
        target_id: data.id,
        origin_url: window.location.origin,
      });
      window.location.href = session.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.error || "Could not create booking");
      setSubmitting(false);
    }
  };

  const submitOrder = async () => {
    setSubmitting(true);
    try {
      const payload = {
        service_id: service.id,
        package_id: pkgId,
        addon_ids: addonIds,
        quantity_map: Object.fromEntries(addonIds.map((id) => [id, 1])),
        deal_id: deal?.id || null,
      };
      const { data } = created.current?.type === "order" ? { data: { id: created.current.id } } : await api.post("/digital-orders", payload);
      created.current = { type: "order", id: data.id };
      const { data: session } = await api.post("/payments/checkout", {
        purpose: "order",
        target_id: data.id,
        origin_url: window.location.origin,
      });
      window.location.href = session.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.error || "Could not create order");
      setSubmitting(false);
    }
  };

  if (!service) {
    return <PublicLayout><div className="mx-auto max-w-3xl px-4 sm:px-6 py-16 sm:py-24 text-[#252A34]/60">Loading checkout…</div></PublicLayout>;
  }

  const onSubmit = service.kind === "digital" ? submitOrder : submitBooking;

  return (
    <PublicLayout>
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10 pt-4 sm:pt-8 pb-32 lg:pb-16 overflow-x-hidden" data-testid="checkout-page">
        <button onClick={() => navigate(-1)} className="text-sm text-[#165a71] font-medium inline-flex items-center gap-2 mb-3 hover:underline" data-testid="checkout-back">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <div className="grid gap-6 lg:gap-8 lg:grid-cols-12">
          <div className="lg:col-span-8 space-y-4 sm:space-y-6 min-w-0">
            <div>
              <div className="text-[11px] sm:text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">
                Checkout · {service.kind === "digital" ? "Digital order" : (service.service_type === "consultation" ? "Consultation" : "Local booking")}
              </div>
              <h1 className="mt-1.5 font-display text-xl sm:text-3xl md:text-4xl font-semibold text-[#071b22] leading-tight break-words" data-testid="checkout-title">{service.title}</h1>
            </div>

            {needsSlot && (
              <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6 min-w-0" data-testid="checkout-slots">
                <h2 className="font-display text-base sm:text-lg font-semibold text-[#071b22] mb-3 inline-flex items-center gap-2">
                  <Clock className="h-4 w-4" /> Pick a date & time
                </h2>
                {slotsLoading ? (
                  <div className="text-sm text-[#252A34]/60">Loading slots…</div>
                ) : slots.length === 0 ? (
                  <div className="text-sm text-[#252A34]/60">Provider hasn't set weekly hours — try messaging them to arrange a custom time.</div>
                ) : (
                  <div>
                    {/* Day picker — horizontally scrollable with snap points on mobile */}
                    <div className="-mx-1 flex gap-2 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-thin" data-testid="day-picker" style={{ scrollbarWidth: "thin" }}>
                      {slots.map((d, i) => (
                        <button
                          key={d.date}
                          onClick={() => { setDayIdx(i); setSelectedSlot(null); }}
                          className={`shrink-0 snap-start ml-1 first:ml-1 px-3 py-2 rounded-xl border text-sm ${i === dayIdx ? "border-[#165a71] bg-[#165a71]/10 text-[#071b22]" : "border-slate-200 hover:bg-slate-50"}`}
                          data-testid={`day-${d.date}`}
                        >
                          <div className="text-[10px] uppercase font-bold tracking-wider">{new Date(d.date + "T00:00:00Z").toLocaleDateString(undefined, { weekday: "short" })}</div>
                          <div className="text-sm">{new Date(d.date + "T00:00:00Z").toLocaleDateString(undefined, { month: "short", day: "numeric" })}</div>
                        </button>
                      ))}
                    </div>
                    {/* Slot grid — fluid 2 cols on phones, 3 on ~500px, 4 on sm+ */}
                    <div
                      className="mt-3 grid gap-2 sm:grid-cols-4"
                      style={{ gridTemplateColumns: "repeat(auto-fill, minmax(6.5rem, 1fr))" }}
                      data-testid="slot-picker"
                    >
                      {(slots[dayIdx]?.slots || []).length === 0 && <div className="col-span-full text-sm text-[#252A34]/60">No slots this day.</div>}
                      {(slots[dayIdx]?.slots || []).map((s) => {
                        const isSelected = selectedSlot?.start === s.start;
                        return (
                          <button
                            key={s.start}
                            disabled={!s.available}
                            onClick={() => setSelectedSlot(s)}
                            data-testid={`slot-${s.start}`}
                            className={`min-w-0 rounded-xl border px-2 py-2 text-sm font-medium transition ${
                              !s.available ? "opacity-40 cursor-not-allowed border-slate-200" :
                              isSelected ? "border-[#165a71] bg-[#165a71] text-white" : "border-slate-200 hover:border-[#165a71]/40"
                            }`}
                          >
                            {new Date(s.start).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </section>
            )}

            {service.kind === "local" && (
              <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6 min-w-0" data-testid="checkout-address">
                <h2 className="font-display text-base sm:text-lg font-semibold text-[#071b22] mb-3 inline-flex items-center gap-2">
                  <MapPin className="h-4 w-4" /> Service address
                </h2>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label="Street address"><input value={addrLine1} onChange={(e) => setAddrLine1(e.target.value)} className="ch-input" data-testid="addr-line1" placeholder="123 Beach Blvd" /></Field>
                  <Field label="Apt / suite"><input value={addrLine2} onChange={(e) => setAddrLine2(e.target.value)} className="ch-input" data-testid="addr-line2" /></Field>
                  <Field label="City"><input value={addrCity} onChange={(e) => setAddrCity(e.target.value)} className="ch-input" data-testid="addr-city" placeholder="Pensacola" /></Field>
                  <Field label="State">
                    <select value={addrState} onChange={(e) => setAddrState(e.target.value)} className="ch-input" data-testid="addr-state">
                      {["FL", "AL", "MS", "LA", "TX"].map((s) => <option key={s}>{s}</option>)}
                    </select>
                  </Field>
                  <Field label="ZIP"><input value={addrZip} onChange={(e) => setAddrZip(e.target.value)} className="ch-input" data-testid="addr-zip" placeholder="32502" /></Field>
                </div>
                <Field label="Access notes"><textarea rows={2} value={accessNotes} onChange={(e) => setAccessNotes(e.target.value)} className="ch-input" placeholder="Gate code, parking, pets…" data-testid="addr-access" /></Field>
              </section>
            )}

            <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6 min-w-0">
              <h2 className="font-display text-base sm:text-lg font-semibold text-[#071b22] mb-3">Instructions for the provider</h2>
              <textarea rows={4} value={instructions} onChange={(e) => setInstructions(e.target.value)} className="ch-input" placeholder="Anything specific to know for this job?" data-testid="checkout-instructions" />
            </section>
          </div>

          {/* Summary card — sticky on desktop, in-flow on mobile */}
          <div className="lg:col-span-4 min-w-0">
            <div className="lg:sticky lg:top-24 space-y-4">
              <div className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6" data-testid="checkout-summary">
                <div className="font-display text-base sm:text-lg font-semibold text-[#071b22]">Order summary</div>
                <div className="mt-3 space-y-2 text-sm">
                  <Row label={pkg?.name || "Base service"} value={`$${pricing.subtotal.toFixed(2)}`} />
                  {addonDocs.map((a) => <Row key={a.id} label={a.name} value={`+$${a.price}`} />)}
                  {pricing.dealOff > 0 && <div data-testid="checkout-deal-row"><Row label={`Deal: ${deal.title}`} value={`-$${pricing.dealOff.toFixed(2)}`} /></div>}
                </div>
                <div className="mt-4 pt-4 border-t border-slate-200 flex items-center justify-between">
                  <div className="text-sm text-[#252A34]/70">Total</div>
                  <div className="font-display text-xl sm:text-2xl font-semibold text-[#071b22]" data-testid="checkout-total">${pricing.total.toFixed(2)}</div>
                </div>
                {/* Desktop / tablet inline pay button. Mobile pay CTA is sticky (below). */}
                <motion.button
                  whileTap={{ scale: 0.98 }}
                  disabled={submitting}
                  onClick={onSubmit}
                  data-testid="checkout-submit"
                  className="hidden sm:block mt-5 w-full rounded-full bg-[#165a71] text-white px-5 py-3 font-medium hover:bg-[#11485a] transition-colors disabled:opacity-60"
                >
                  {submitting ? "Opening secure checkout…" : "Continue to secure payment"}
                </motion.button>
                <div className="mt-3 text-[11px] text-[#252A34]/60 flex items-start gap-2">
                  <ShieldCheck className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  <span>Powered by Stripe · Test mode. Provider payout is released according to the service completion policy.</span>
                </div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-xs text-[#252A34]/70">
                <div className="font-medium text-[#252A34] mb-1 inline-flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> What happens next</div>
                <ol className="list-decimal ml-4 mt-1 space-y-1">
                  <li>You'll be redirected to Stripe's secure test-mode checkout.</li>
                  <li>Use test card <span className="font-mono">4242 4242 4242 4242</span>, any future expiry, any CVC.</li>
                  <li>On success, your workspace opens and the provider is notified.</li>
                </ol>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sticky bottom pay CTA — mobile only */}
      <div className="sm:hidden fixed inset-x-0 bottom-0 z-30 bg-white/95 backdrop-blur border-t border-slate-200 px-4 py-3" data-testid="checkout-sticky-pay">
        <div className="flex items-center gap-3">
          <div className="min-w-0">
            <div className="text-[11px] text-[#252A34]/60 uppercase tracking-wider font-medium">Total</div>
            <div className="font-display text-lg font-semibold text-[#071b22]" data-testid="checkout-sticky-total">${pricing.total.toFixed(2)}</div>
          </div>
          <motion.button
            whileTap={{ scale: 0.97 }}
            disabled={submitting}
            onClick={onSubmit}
            data-testid="checkout-submit-mobile"
            className="ml-auto flex-1 rounded-full bg-[#165a71] text-white px-4 py-3 text-sm font-medium disabled:opacity-60"
          >
            {submitting ? "Opening…" : "Continue to pay"}
          </motion.button>
        </div>
      </div>

      <style>{`
        .ch-input { width: 100%; border-radius: 0.75rem; border: 1px solid #e2e8f0; background: white; padding: 0.6rem 0.75rem; font-size: 0.875rem; outline: none; }
        .ch-input:focus { border-color: #165a71; box-shadow: 0 0 0 3px rgba(22, 90, 113, 0.12); }
      `}</style>
    </PublicLayout>
  );
}

const Field = ({ label, children }) => (
  <label className="block">
    <div className="text-xs font-medium text-[#252A34]/70 mb-1">{label}</div>
    {children}
  </label>
);
const Row = ({ label, value }) => (
  <div className="flex items-center justify-between text-[#252A34] gap-3">
    <span className="text-[#252A34]/70 truncate min-w-0">{label}</span>
    <span className="font-medium shrink-0">{value}</span>
  </div>
);
